import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import { TrendingDown } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { projectVncSeries, DEFAULT_VNC_HORIZON_MONTHS } from '../../utils/vncProjection';

/**
 * Mini-graphique : projection de la VNC totale sur 36 mois, empilée par
 * catégorie (Flotte / Dépôts / Bureau / Logiciels / Site internet).
 *
 * Source de vérité : `projectVncSeries` (utils/vncProjection.js) qui
 * appelle les mêmes moteurs `computeFleetDepreciation` et
 * `computeCapexDepreciation` que le bloc Amortissements → cohérence
 * parfaite : la VNC d'aujourd'hui dans le chart = la VNC affichée
 * dans le tableau.
 *
 * 28/05/2026 v2 — Phase B bonus.
 */

const CATEGORY_DEFS = [
  { key: 'fleet',    label: 'Flotte',         color: '#0EA5E9' },
  { key: 'depot',    label: 'Dépôts',         color: '#10B981' },
  { key: 'office',   label: 'Bureau/Siège',   color: '#A78BFA' },
  { key: 'software', label: 'Logiciels',      color: '#F59E0B' },
  { key: 'website',  label: 'Site internet',  color: '#EC4899' },
];

export default function VncEvolutionChart() {
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const activeOptimizations = useGameStore(s => s.activeOptimizations || EMPTY_OBJECT);
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  const investmentPurchaseDays = useGameStore(s => s.investmentPurchaseDays || EMPTY_OBJECT);
  const officeUpgrades = useGameStore(s => s.officeUpgrades || EMPTY_OBJECT);

  const data = useMemo(
    () => projectVncSeries(
      { vehicles, totalDays, activeOptimizations, activeInvestments, investmentPurchaseDays, officeUpgrades },
      DEFAULT_VNC_HORIZON_MONTHS,
    ),
    [vehicles, totalDays, activeOptimizations, activeInvestments, investmentPurchaseDays, officeUpgrades]
  );

  const hasAny = data.length > 0 && data[0].total > 0;

  // Pick which categories actually have data → don't render empty stack layers.
  const activeCategories = useMemo(() => {
    if (!hasAny) return [];
    return CATEGORY_DEFS.filter(cat => data.some(d => d[cat.key] > 0));
  }, [data, hasAny]);

  if (!hasAny) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-ink-600 p-6 text-center"
        data-testid="vnc-evolution-empty"
      >
        <TrendingDown className="w-5 h-5 mx-auto text-slate-500 mb-2" />
        <p className="text-sm text-slate-500">
          Aucune immobilisation à projeter.
        </p>
        <p className="text-[10px] text-slate-500 mt-1 italic">
          Le graphique apparaîtra dès le 1ᵉʳ véhicule acheté ou la 1ʳᵉ immobilisation activée.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="vnc-evolution-chart">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-cyan-300" />
          <p className="text-sm font-semibold text-slate-200">Évolution VNC — projection 36 mois</p>
        </div>
        <p className="text-[10px] text-slate-500">
          Hypothèse : aucun nouvel achat ni cession. Mode {activeOptimizations.amortissement_accelere ? 'accéléré' : 'linéaire'} appliqué sur la flotte.
        </p>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
          <YAxis
            stroke="#9CA3AF"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => `${Math.round(v / 1000)}k`}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
            formatter={(value, name) => [`${Math.round(value).toLocaleString('fr-FR')} €`, name]}
            labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {activeCategories.map(cat => (
            <Area
              key={cat.key}
              type="monotone"
              dataKey={cat.key}
              name={cat.label}
              stackId="vnc"
              stroke={cat.color}
              fill={cat.color}
              fillOpacity={0.55}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {/* Pedagogical caption */}
      <div className="mt-2 p-2.5 rounded bg-cyan-900/15 border border-cyan-700/20 text-[11px] text-slate-400 leading-relaxed">
        <strong className="text-cyan-300">📉 Lecture :</strong> la décrue de chaque couche illustre
        la vitesse d&apos;amortissement comptable. Les <strong className="text-pink-300">sites internet</strong> (2 ans)
        et <strong className="text-amber-300">logiciels</strong> (3 ans) plongent vite — anticipez leur remplacement.
        Les <strong className="text-emerald-300">dépôts</strong> (20 ans) restent quasi plats sur l&apos;horizon.
        VNC actuelle&nbsp;: <strong className="text-slate-200">{data[0].total.toLocaleString('fr-FR')} €</strong>
        {' '}→ projetée à 36 mois&nbsp;:{' '}
        <strong className="text-cyan-300">{data[data.length - 1].total.toLocaleString('fr-FR')} €</strong>
        {' '}(<span className="text-amber-300">−{(data[0].total - data[data.length - 1].total).toLocaleString('fr-FR')} €</span> de dotations).
      </div>
    </div>
  );
}
